import React, { createContext, useState, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';

interface MapContextType {
  map: mapboxgl.Map | null;
  highlightLocation: (location: any) => void;
  clearHighlights: () => void;
}

export const MapContext = createContext<MapContextType | null>(null);

interface MapProviderProps {
  map: mapboxgl.Map | null;
  children: React.ReactNode;
}

export const MapProvider: React.FC<MapProviderProps> = ({ map, children }) => {
  const [highlightedMarker, setHighlightedMarker] = useState<mapboxgl.Marker | null>(null);

  const highlightLocation = (location: any) => {
    if (!map) return;
    
    // Extract coordinates from location object, checking different possible property names
    let coordinates: [number, number] | null = null;
    
    // Check for different possible coordinate formats in the location object
    if (location.longitude !== undefined && location.latitude !== undefined) {
      coordinates = [Number(location.longitude), Number(location.latitude)];
    } else if (location.lng !== undefined && location.lat !== undefined) {
      coordinates = [Number(location.lng), Number(location.lat)];
    } else if (location.coordinates && Array.isArray(location.coordinates) && location.coordinates.length >= 2) {
      coordinates = [Number(location.coordinates[0]), Number(location.coordinates[1])];
    } else if (location.geoPoint) {
      // Handle possible nested geoPoint object
      const geo = location.geoPoint;
      if (geo.longitude !== undefined && geo.latitude !== undefined) {
        coordinates = [Number(geo.longitude), Number(geo.latitude)];
      } else if (geo.lng !== undefined && geo.lat !== undefined) {
        coordinates = [Number(geo.lng), Number(geo.lat)];
      } else if (Array.isArray(geo) && geo.length >= 2) {
        coordinates = [Number(geo[0]), Number(geo[1])];
      }
    } else if (location.geoPin) {
      // Try geoPin object
      const geoPin = location.geoPin;
      if (geoPin.longitude !== undefined && geoPin.latitude !== undefined) {
        coordinates = [Number(geoPin.longitude), Number(geoPin.latitude)];
      } else if (geoPin.lng !== undefined && geoPin.lat !== undefined) {
        coordinates = [Number(geoPin.lng), Number(geoPin.lat)];
      } else if (Array.isArray(geoPin) && geoPin.length >= 2) {
        coordinates = [Number(geoPin[0]), Number(geoPin[1])];
      }
    } else if (location.geopin) {
      // Try lowercase geopin
      const geopin = location.geopin;
      if (geopin.longitude !== undefined && geopin.latitude !== undefined) {
        coordinates = [Number(geopin.longitude), Number(geopin.latitude)];
      } else if (geopin.lng !== undefined && geopin.lat !== undefined) {
        coordinates = [Number(geopin.lng), Number(geopin.lat)];
      } else if (Array.isArray(geopin) && geopin.length >= 2) {
        coordinates = [Number(geopin[0]), Number(geopin[1])];
      }
    }
    
    // Debug info
    console.log('Location object:', location);
    console.log('Extracted coordinates:', coordinates);
    
    // If we couldn't find valid coordinates, try to use hardcoded values for the city
    if (!coordinates || coordinates.some(isNaN)) {
      console.warn('Could not find valid coordinates in location object, trying city lookup');
      
      // Simple city to coordinates mapping for common cities
      const cityCoordinates: {[key: string]: [number, number]} = {
        'New York': [-74.006, 40.7128],
        'San Francisco': [-122.4194, 37.7749],
        'London': [-0.1278, 51.5074],
        'Tokyo': [139.6503, 35.6762],
        'Paris': [2.3522, 48.8566],
        'Berlin': [13.4050, 52.5200],
        'Sydney': [151.2093, -33.8688],
        'Chicago': [-87.6298, 41.8781],
        'Mumbai': [72.8777, 19.0760],
        'Singapore': [103.8198, 1.3521]
      };
      
      if (location.city && cityCoordinates[location.city]) {
        coordinates = cityCoordinates[location.city];
        console.log('Using hardcoded coordinates for', location.city, coordinates);
      } else {
        console.error('Invalid coordinates and no city match found for location:', location);
        return;
      }
    }
    
    // Clear any existing highlighted marker
    clearHighlights();
    
    // Fly to the location
    map.flyTo({
      center: coordinates,
      zoom: 12,
      essential: true
    });
    
    // Create a temporary highlighted marker
    const marker = new mapboxgl.Marker({
      color: '#4a90e2',
      scale: 1.2
    })
      .setLngLat(coordinates)
      .addTo(map);
    
    // Add highlight class for styling
    const markerElement = marker.getElement();
    markerElement.classList.add('highlighted');
    
    setHighlightedMarker(marker);
    
    // Auto-clear after 5 seconds
    setTimeout(() => {
      if (marker) {
        marker.remove();
        setHighlightedMarker(null);
      }
    }, 5000);
  };
  
  const clearHighlights = () => {
    if (highlightedMarker) {
      highlightedMarker.remove();
      setHighlightedMarker(null);
    }
  };
  
  // Clear highlights on unmount
  useEffect(() => {
    return () => {
      clearHighlights();
    };
  }, []);
  
  return (
    <MapContext.Provider value={{ map, highlightLocation, clearHighlights }}>
      {children}
    </MapContext.Provider>
  );
};